// VenueComparisonTable — the per-venue summary from venue_comparison.summary:
// Notional · Premium · Avg ticket · Block share · Net · Share%. Consistent venue
// swatch (shared with the stacked bar), a T-1 chip for shallow/lagging venues,
// and click-to-sort on the numeric columns (default: notional share, desc).

import { useMemo, useState } from 'react';
import { Box } from '@mui/material';

import { getTokenValue, tokens } from '../../../../styles/tokens';
import { useThemeMode } from '../../../../providers/theme-provider';
import {
  fmtPercent,
  fmtUsd,
  fmtCount,
  venueColorToken,
} from '../../../../providers/options-flow/options-flow.helpers';
import { VenueSummaryRow } from '../../../../providers/options-flow/types';

const MONO = 'ui-monospace, Menlo, Consolas, monospace';

type NumKey = Exclude<keyof VenueSummaryRow, 'venue'>;
const COLUMNS: { key: NumKey; label: string; fmt: (v: number) => string }[] = [
  { key: 'notional_usd', label: 'Notional', fmt: fmtUsd },
  { key: 'premium_usd', label: 'Premium', fmt: fmtUsd },
  { key: 'avg_ticket_usd', label: 'Avg ticket', fmt: fmtUsd },
  { key: 'trades', label: 'Trades', fmt: fmtCount },
  { key: 'block_share_pct', label: 'Block %', fmt: fmtPercent },
  { key: 'net_premium_usd', label: 'Net', fmt: fmtUsd },
  { key: 'share_notional_pct', label: 'Share %', fmt: fmtPercent },
];

const thText = {
  fontSize: 9.5,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.4px',
  color: tokens.text.muted,
};

export const VenueComparisonTable = ({
  summary,
  tMinusOne,
  coverageNote,
}: {
  summary: VenueSummaryRow[];
  tMinusOne?: Set<string>;
  coverageNote?: string;
}) => {
  const { resolvedMode } = useThemeMode();
  const buy = getTokenValue('flow.buy', resolvedMode);
  const sell = getTokenValue('flow.sell', resolvedMode);

  const [sortKey, setSortKey] = useState<NumKey>('share_notional_pct');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');

  const rows = useMemo(() => {
    const sorted = [...summary].sort((a, b) => (a[sortKey] ?? 0) - (b[sortKey] ?? 0));
    return dir === 'desc' ? sorted.reverse() : sorted;
  }, [summary, sortKey, dir]);

  const onSort = (key: NumKey) => {
    if (key === sortKey) setDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setDir('desc');
    }
  };

  // 1 label column + N numeric columns.
  const gridTemplateColumns = `minmax(96px, 1.2fr) repeat(${COLUMNS.length}, minmax(64px, 1fr))`;

  return (
    <Box sx={{ mt: 2, overflowX: 'auto' }}>
      <Box sx={{ minWidth: 640 }}>
        {/* Header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns,
            alignItems: 'center',
            columnGap: '8px',
            px: '6px',
            py: '4px',
            borderBottom: `1px solid ${tokens.border.subtle}`,
          }}
        >
          <Box sx={thText}>Venue</Box>
          {COLUMNS.map((c) => (
            <Box
              key={c.key}
              role="button"
              onClick={() => onSort(c.key)}
              sx={{
                ...thText,
                textAlign: 'right',
                cursor: 'pointer',
                color: c.key === sortKey ? tokens.text.secondary : tokens.text.muted,
                userSelect: 'none',
              }}
            >
              {c.label}
              {c.key === sortKey ? (dir === 'desc' ? ' ↓' : ' ↑') : ''}
            </Box>
          ))}
        </Box>

        {/* Rows */}
        {rows.map((r) => (
          <Box
            key={r.venue}
            sx={{
              display: 'grid',
              gridTemplateColumns,
              alignItems: 'center',
              columnGap: '8px',
              px: '6px',
              py: '5px',
              borderBottom: `1px solid ${tokens.border.subtle}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              <Box
                sx={{
                  width: 9,
                  height: 9,
                  borderRadius: '2px',
                  flex: '0 0 auto',
                  backgroundColor: getTokenValue(venueColorToken(r.venue), resolvedMode),
                }}
              />
              <Box
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: tokens.text.primary,
                  textTransform: 'capitalize',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.venue}
              </Box>
              {tMinusOne?.has(r.venue) && (
                <Box
                  sx={{
                    fontSize: 8.5,
                    fontWeight: 700,
                    letterSpacing: '0.3px',
                    color: tokens.text.muted,
                    border: `1px solid ${tokens.border.default}`,
                    borderRadius: '3px',
                    px: '3px',
                    flex: '0 0 auto',
                  }}
                >
                  T-1
                </Box>
              )}
            </Box>
            {COLUMNS.map((c) => {
              const raw = r[c.key];
              const isNet = c.key === 'net_premium_usd';
              const color = isNet
                ? raw >= 0
                  ? buy
                  : sell
                : c.key === sortKey
                  ? tokens.text.primary
                  : tokens.text.secondary;
              return (
                <Box
                  key={c.key}
                  sx={{
                    textAlign: 'right',
                    fontFamily: MONO,
                    fontSize: 11.5,
                    fontWeight: c.key === sortKey || isNet ? 700 : 500,
                    color,
                  }}
                >
                  {c.fmt(raw)}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Coverage caveat (24h): the stale/t-1 venues make the share numbers a
          latency artifact — surfaced so ~96% Deribit isn't misread as real. */}
      {coverageNote && (
        <Box sx={{ mt: 1, fontSize: 10, color: tokens.text.muted, lineHeight: 1.5 }}>
          {coverageNote}
        </Box>
      )}
    </Box>
  );
};
